import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Eye, Pencil, Mail } from "lucide-react";

const inputCls =
  "w-full bg-transparent border px-4 py-3 text-sm text-bone placeholder:text-muted-bone outline-none";
const borderStyle = { borderColor: "var(--rs-border)" };
const btnPrimary =
  "px-6 py-3 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition disabled:opacity-40";

const AdminJournal = () => {
  const [posts, setPosts] = useState(null);
  const [products, setProducts] = useState([]);
  const [topic, setTopic] = useState("");
  const [productSlug, setProductSlug] = useState("");
  const [publishNow, setPublishNow] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [automation, setAutomation] = useState(null);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/journal").then((r) => setPosts(r.data)).catch(() => setPosts([]));
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/automation").then((r) => setAutomation(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/journal/generate", {
        topic: topic || null,
        product_slug: productSlug || null,
        publish: publishNow,
      });
      toast.success(`Drafted: ${data.title}`);
      setTopic("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (post) => {
    await api.patch(`/admin/journal/${post.post_id}`, { published: !post.published });
    load();
  };

  const remove = async (postId) => {
    await api.delete(`/admin/journal/${postId}`);
    toast.success("Post deleted");
    load();
  };

  const openEdit = async (post) => {
    const { data } = await api.get(`/admin/journal/${post.post_id}`);
    setEditing(data);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/journal/${editing.post_id}`, {
        title: editing.title,
        excerpt: editing.excerpt,
        content_html: editing.content_html,
        meta_description: editing.meta_description,
      });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleDailyAutomation = async () => {
    const { data } = await api.patch("/admin/automation", {
      journal_daily_enabled: !automation.journal_daily_enabled,
    });
    setAutomation(data);
    toast.success(data.journal_daily_enabled ? "Daily journal automation ON" : "Daily journal automation OFF");
  };

  const sendNewsletter = async () => {
    setSendingNewsletter(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { auto_generate: true });
      toast.success(`memoirs queued to ${data.queued} subscribers — "${data.subject}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Newsletter failed");
    } finally {
      setSendingNewsletter(false);
    }
  };

  return (
    <div data-testid="admin-journal">
      <div className="overline mb-3">The Journal — SEO Engine</div>
      <h1 className="font-serif-display text-5xl text-bone mb-10">Letters from the house.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <div className="lg:col-span-2 border p-6 space-y-4" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
          <div className="overline flex items-center gap-2"><Sparkles size={14} /> Generate a new piece</div>
          <input
            data-testid="journal-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic (optional — leave blank and the house will choose)"
            className={inputCls}
            style={borderStyle}
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              data-testid="journal-product-select"
              value={productSlug}
              onChange={(e) => setProductSlug(e.target.value)}
              className={`${inputCls} flex-1`}
              style={{ ...borderStyle, background: "var(--rs-surface)" }}
            >
              <option value="">No featured product</option>
              {products.map((p) => (
                <option key={p.product_id} value={p.slug}>{p.name}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-bone cursor-pointer">
              <input
                data-testid="journal-publish-checkbox"
                type="checkbox"
                checked={publishNow}
                onChange={(e) => setPublishNow(e.target.checked)}
              />
              Publish immediately
            </label>
          </div>
          <button
            data-testid="journal-generate-btn"
            onClick={generate}
            disabled={generating}
            className={btnPrimary}
            style={{ background: "var(--rs-oxblood)" }}
          >
            {generating ? "The house is writing… (30–60s)" : "Generate article"}
          </button>
        </div>

        <div className="border p-6 space-y-5" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
          <div className="overline flex items-center gap-2"><Mail size={14} /> memoirs & automation</div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-bone">Daily auto-publish + email</span>
            <button
              data-testid="daily-automation-toggle"
              onClick={toggleDailyAutomation}
              className={`px-3 py-1 text-[10px] uppercase tracking-widest ${automation?.journal_daily_enabled ? "text-bone" : "text-muted-bone border"}`}
              style={automation?.journal_daily_enabled ? { background: "#2f6f4f" } : borderStyle}
            >
              {automation?.journal_daily_enabled ? "On" : "Off"}
            </button>
          </div>
          <button
            data-testid="send-newsletter-btn"
            onClick={sendNewsletter}
            disabled={sendingNewsletter}
            className={`w-full ${btnPrimary}`}
            style={{ background: "var(--rs-oxblood)" }}
          >
            {sendingNewsletter ? "Composing…" : "Send memoirs now"}
          </button>
          <p className="text-xs text-muted-bone leading-relaxed">
            AI composes a "memoirs" edition from the latest journal piece and featured products, then emails all subscribers.
          </p>
        </div>
      </div>

      <div className="overline mb-4">All posts</div>
      {posts === null ? (
        <div className="text-muted-bone">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
          Nothing written yet. Generate the first piece above.
        </div>
      ) : (
        <div className="border" style={borderStyle}>
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                <th className="text-left px-6 py-4">Title</th>
                <th className="text-left px-6 py-4 hidden md:table-cell">Created</th>
                <th className="text-left px-6 py-4">Status</th>
                <th className="text-right px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.post_id} data-testid={`journal-row-${p.post_id}`} className="border-t text-sm" style={borderStyle}>
                  <td className="px-6 py-4">
                    <div className="text-bone">{p.title}</div>
                    <div className="text-xs text-muted-bone">/journal/{p.slug}</div>
                  </td>
                  <td className="px-6 py-4 text-muted-bone hidden md:table-cell">{p.created_at?.slice(0, 10)}</td>
                  <td className="px-6 py-4">
                    <button
                      data-testid={`journal-publish-toggle-${p.post_id}`}
                      onClick={() => togglePublish(p)}
                      className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
                      style={{ background: p.published ? "#2f6f4f" : "#555" }}
                    >
                      {p.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-4">
                      {p.published && (
                        <a
                          data-testid={`journal-view-${p.post_id}`}
                          href={`/journal/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-muted-bone hover:text-bone"
                          aria-label="View"
                        >
                          <Eye size={15} />
                        </a>
                      )}
                      <button
                        data-testid={`journal-edit-${p.post_id}`}
                        onClick={() => openEdit(p)}
                        className="text-muted-bone hover:text-bone"
                        aria-label="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        data-testid={`journal-delete-${p.post_id}`}
                        onClick={() => remove(p.post_id)}
                        className="text-muted-bone hover:text-bone"
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.8)" }}>
          <div
            data-testid="journal-edit-modal"
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto border p-8 space-y-4"
            style={{ ...borderStyle, background: "var(--rs-surface)" }}
          >
            <div className="overline">Edit post</div>
            <input
              data-testid="edit-title-input"
              value={editing.title}
              onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
              className={inputCls}
              style={borderStyle}
            />
            <textarea
              data-testid="edit-excerpt-input"
              value={editing.excerpt}
              onChange={(e) => setEditing((p) => ({ ...p, excerpt: e.target.value }))}
              rows={2}
              className={inputCls}
              style={borderStyle}
            />
            <textarea
              data-testid="edit-content-input"
              value={editing.content_html}
              onChange={(e) => setEditing((p) => ({ ...p, content_html: e.target.value }))}
              rows={16}
              className={`${inputCls} font-mono text-xs`}
              style={borderStyle}
            />
            <div className="flex justify-end gap-4">
              <button
                data-testid="edit-cancel-btn"
                onClick={() => setEditing(null)}
                className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
              >
                Cancel
              </button>
              <button
                data-testid="edit-save-btn"
                onClick={saveEdit}
                disabled={saving}
                className={btnPrimary}
                style={{ background: "var(--rs-oxblood)" }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJournal;
